import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { settingsApi, type DownloadsKind } from "@/api/settings";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";

const LABELS: Record<DownloadsKind, string> = {
  movies: "Movies",
  tv: "TV",
  music: "Music",
};

/**
 * Pause switch for one media type, shown at the top of its settings
 * page. Saves on click rather than with the page's form: pausing is an
 * "act now" control and shouldn't wait on unrelated unsaved fields.
 */
export function DownloadsEnabledCard({ kind }: { kind: DownloadsKind }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const label = LABELS[kind];

  const { data, isLoading, isError } = useQuery({
    queryKey: ["settings", "downloads-enabled"],
    queryFn: settingsApi.downloadsEnabled.get,
  });

  const mut = useMutation({
    mutationFn: (enabled: boolean) =>
      settingsApi.downloadsEnabled.set({ [kind]: enabled }),
    onSuccess: (res) => {
      qc.setQueryData(["settings", "downloads-enabled"], res);
      toast(
        res[kind] ? `${label} downloads resumed` : `${label} downloads paused`,
        "success",
      );
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Update failed", "error"),
  });

  const enabled = data?.[kind];

  return (
    <div className="rounded-md border border-line bg-surface/50 px-5 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1.5">
          <div className="eyebrow">Downloads</div>
          <div className="text-sm">
            {isLoading ? (
              <Skeleton.Block height="1rem" width="10rem" />
            ) : isError || enabled === undefined ? (
              <span className="text-foreground/55">
                Couldn&apos;t load the download status.
              </span>
            ) : enabled ? (
              <Status tone="good">Enabled</Status>
            ) : (
              <Status tone="warn">Paused</Status>
            )}
          </div>
          <p className="max-w-prose text-xs text-foreground/55">
            Pausing stops new automatic grabs and the one-click search for{" "}
            {kind === "tv" ? "TV" : kind}. Picking a specific release by hand
            still works, and downloads already in the client keep going.
            {kind !== "music" &&
              " Anything added while paused isn't searched automatically after you resume — search it from the Wanted page."}
          </p>
        </div>
        {enabled !== undefined && (
          <Button
            type="button"
            variant={enabled ? "secondary" : "primary"}
            onClick={() => mut.mutate(!enabled)}
            loading={mut.isPending}
          >
            {enabled ? "Pause downloads" : "Resume downloads"}
          </Button>
        )}
      </div>
    </div>
  );
}
